<script setup lang="ts">
import { computed } from 'vue'
import OkaneKollegeUserListItemHanshaChip from './OkaneKollegeUserListItemHanshaChip.vue'
import OkaneKollegeUserListItemUnActivatedChip from './OkaneKollegeUserListItemUnActivatedChip.vue'
import UserCourseStatusChip from '@/features/okane-kollege/UserCourseStatusChip/UserCourseStatusChip.vue'
import UserTrialStatusChip from '@/features/okane-kollege/UserTrialStatusChip/UserTrialStatusChip.vue'
import CalendarIcon from '@/assets/images/icons/calendar.svg?component'
import type { OkaneKollegeUserListItemKollegeUserFragment } from './OkaneKollegeUserListItem.generated'
import { format } from 'date-fns'
import { ja } from 'date-fns/locale/ja'
import { useRoute } from 'vue-router'

const props = defineProps<{
  user: OkaneKollegeUserListItemKollegeUserFragment
}>()

const route = useRoute('OkaneKollegeUser')

const userName = computed(() => {
  if (props.user.profile == null) {
    return '名前なしユーザー'
  }
  return `${props.user.profile.familyName} ${props.user.profile.givenName}`
})

const link = computed(() => {
  return {
    name: 'OkaneKollegeUser' as const,
    params: {
      entranceGUID: route.params.entranceGUID,
      userId: props.user.id,
    },
  }
})

const toDatePeriod = (period: { startsAt: TimeString; endsAt: TimeString }) => {
  const startsAt = new Date(period.startsAt)
  const endsAt = new Date(period.endsAt)

  return {
    startsAt,
    endsAt,
  }
}

const formatPeriod = (period: { startsAt: Date; endsAt: Date }) => {
  const startsAt = new Date(period.startsAt)
  const endsAt = new Date(period.endsAt)

  const formatDate = (d: Date) =>
    format(d, 'yyyy/MM/dd', {
      locale: ja,
    })
  return `${formatDate(startsAt)} 〜 ${formatDate(endsAt)}`
}

const userCoursePeriodText = computed(() => {
  const course = props.user.standardCourse
  if (!course?.period) {
    return '未定'
  }

  const period = toDatePeriod(course.period)
  return formatPeriod(period)
})

const dailyLessonPeriodText = computed(() => {
  const rawPeriod = props.user.standardCourse?.expenseRecordDailyLesson?.period
  if (!rawPeriod) {
    return '未定'
  }

  const period = toDatePeriod(rawPeriod)
  if (period.endsAt < new Date()) {
    return '受講終了'
  }
  return formatPeriod(period)
})

const lastLessonDateText = computed(() => {
  const lastLessonDate =
    props.user.standardCourse?.lastCompletedLesson?.schedule?.startsAt
  if (!lastLessonDate) {
    return null
  }

  const date = new Date(lastLessonDate)
  return format(date, 'yyyy年MM月dd日 HH:mm')
})
</script>

<template>
  <RouterLink :to="link" :class="$style.item">
    <ul :class="[$style.item__chips, $style.chips]">
      <li v-if="user.isAntiSocial"><OkaneKollegeUserListItemHanshaChip /></li>
      <li v-if="user.email != null && !user.email.isActivated">
        <OkaneKollegeUserListItemUnActivatedChip />
      </li>
      <li>
        <UserTrialStatusChip :user="user" />
      </li>
      <li>
        <UserCourseStatusChip :user="user" />
      </li>
    </ul>
    <p :class="$style.item__name">{{ userName }}</p>
    <dl :class="$style.item__periods">
      <div :class="$style.period">
        <dt :class="$style.period__title">
          <CalendarIcon :class="$style.icon" />受講期間
        </dt>
        <dd :class="$style.period__value">{{ userCoursePeriodText }}</dd>
      </div>
      <div :class="$style.period">
        <dt :class="$style.period__title">
          <CalendarIcon :class="$style.icon" />デイリーレッスン
        </dt>
        <dd :class="$style.period__value">{{ dailyLessonPeriodText }}</dd>
      </div>
    </dl>
    <div :class="[$style.item__latest, $style.latest]">
      <dt :class="$style.latest__title">最終レッスン参加</dt>
      <dd v-if="lastLessonDateText" :class="$style.latest__value">
        {{ lastLessonDateText }}
      </dd>
      <dd v-else :class="$style.latest__empty">未定</dd>
    </div>
  </RouterLink>
</template>

<style lang="scss" module>
.item {
  display: grid;
  grid-template:
    'chips' 24px
    'name' 1fr
    'periods' auto
    'latest' auto
    / 1fr;
  gap: 16px;
  padding: 24px;
  border-radius: $border-radius-v2-rounder;
  box-shadow: $shadow-8;
  color: $color-text-and-object-high-emphasis;

  &:hover {
    text-decoration: none;
  }

  &__chips {
    grid-area: chips;
  }

  &__name {
    grid-area: name;
    font-size: 20px;
    font-weight: bold;
  }

  &__periods {
    grid-area: periods;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__latest {
    grid-area: latest;
  }
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0;
  margin: 0;
  list-style: none;
}

.period {
  display: flex;
  align-items: center;
  gap: 8px;

  &__title {
    display: flex;
    align-items: center;
    gap: 4px;

    &::after {
      content: ':';
    }
  }

  &__value {
    font-weight: bold;
  }
}

.icon {
  fill: currentcolor;
}

.latest {
  display: flex;
  align-items: center;
  gap: 4px;
  border: 1px solid $color-border-low-emphasis;
  border-radius: $border-radius-v2-inner;
  padding: 16px;

  &__title {
    &::after {
      content: ':';
    }
  }

  &__value {
    font-weight: bold;
    color: $color-kollege-text;
  }

  &__empty {
    color: $color-text-and-object-high-emphasis;
  }
}
</style>
